import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import authApi from "../../../service/IdApi";
import PageLoading from "../../../components/PageLoading";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";

const STATUS_OPTIONS = [
    { label: "All statuses", value: "" },
    { label: "PENDING", value: "PENDING" },
    { label: "IN_PROGRESS", value: "IN_PROGRESS" },
    { label: "COMPLETED", value: "COMPLETED" },
    { label: "FAILED", value: "FAILED" },
];

const TYPE_OPTIONS = [
    { label: "All types", value: "" },
    { label: "HERO_SECTION_BACKGROUND_IMAGE", value: "HERO_SECTION_BACKGROUND_IMAGE" },
    { label: "IMAGE_ELEMENT", value: "IMAGE_ELEMENT" },
    { label: "ABOUT_DESCRIPTION", value: "ABOUT_DESCRIPTION" },
    { label: "ABOUT_TEASER", value: "ABOUT_TEASER" },
    { label: "PROFILE_PIC", value: "PROFILE_PIC" },
    { label: "OFFER_COPY", value: "OFFER_COPY" },
    { label: "OFFER_ICON", value: "OFFER_ICON" },
    { label: "PAGE_META_DESCRIPTION", value: "PAGE_META_DESCRIPTION" },
    { label: "SUGGESTED_CATEGORIES", value: "SUGGESTED_CATEGORIES" },
];

const OWNER_OPTIONS = [
    { label: "All owners", value: "" },
    { label: "auth", value: "auth" },
    { label: "provider", value: "provider" },
];

const HAS_ERROR_OPTIONS = [
    { label: "Any error state", value: null },
    { label: "Has error message", value: true },
    { label: "No error message", value: false },
];

const DATE_OPTIONS = [
    { label: "Any date", value: "" },
    { label: "Today", value: "today" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 days", value: "last7" },
    { label: "Last 30 days", value: "last30" },
];

const FILTER_FIELDS = [
    { key: "date", label: "Date", placeholder: "Date", options: DATE_OPTIONS },
    { key: "status", label: "Status", placeholder: "Status", options: STATUS_OPTIONS },
    { key: "type", label: "Type", placeholder: "Type", options: TYPE_OPTIONS },
    { key: "owner", label: "Owner", placeholder: "Owner", options: OWNER_OPTIONS },
    { key: "hasError", label: "Has error", placeholder: "Has error", options: HAS_ERROR_OPTIONS },
    { key: "providerId", label: "Provider ID", placeholder: "ObjectId", input: true },
    { key: "errorText", label: "Error contains", placeholder: "error text", input: true },
];

const DEFAULT_VISIBLE_FILTER_KEYS = ["date"];

const createDefaultFilters = () => ({
    date: "last7",
    status: "",
    type: "",
    owner: "",
    hasError: null,
    providerId: "",
    errorText: "",
});

const emptyFilterValue = (key) => {
    if (key === "hasError") {
        return null;
    }
    if (key === "date") {
        return "";
    }
    return "";
};

const findFilterField = (key) => {
    for (let i = 0; i < FILTER_FIELDS.length; i++) {
        if (FILTER_FIELDS[i].key === key) {
            return FILTER_FIELDS[i];
        }
    }
    return null;
};

const startOfLocalDay = (date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const resolveCreatedDateRange = (preset) => {
    if (!preset) {
        return null;
    }
    const todayStart = startOfLocalDay(new Date());
    const dayMs = 24 * 60 * 60 * 1000;
    if (preset === "today") {
        return {
            createdFrom: todayStart.getTime(),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    if (preset === "yesterday") {
        return {
            createdFrom: todayStart.getTime() - dayMs,
            createdTo: todayStart.getTime(),
        };
    }
    if (preset === "last7") {
        return {
            createdFrom: todayStart.getTime() - (6 * dayMs),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    if (preset === "last30") {
        return {
            createdFrom: todayStart.getTime() - (29 * dayMs),
            createdTo: todayStart.getTime() + dayMs,
        };
    }
    return null;
};

const formatDate = (value) => {
    if (!value) {
        return "";
    }
    try {
        return new Date(value).toLocaleString();
    } catch (e) {
        return String(value);
    }
};

const formatCostUsd = (value) => {
    if (value == null || value === "" || Number.isNaN(Number(value))) {
        return "—";
    }
    return `$${Number(value).toFixed(4)}`;
};

const formatDurationSeconds = (durationMs) => {
    if (durationMs == null || durationMs === "" || Number.isNaN(Number(durationMs))) {
        return "—";
    }
    return (Number(durationMs) / 1000).toFixed(2);
};

const STEP_TYPE_LABELS = {
    tg: "Text generation",
    ig: "Image generation",
    ia: "Image analysis",
    cv: "Create variants",
};

const formatStepType = (type) => {
    if (!type) {
        return "—";
    }
    return STEP_TYPE_LABELS[type] || type;
};

const statusSeverity = (status) => {
    if (status === "FAILED") {
        return "danger";
    }
    if (status === "COMPLETED") {
        return "success";
    }
    if (status === "IN_PROGRESS") {
        return "info";
    }
    return "warning";
};

const AiTasks = () => {
    const [items, setItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [filters, setFilters] = useState(createDefaultFilters);
    const [textInputs, setTextInputs] = useState({
        providerId: "",
        errorText: "",
    });
    const [visibleFilterKeys, setVisibleFilterKeys] = useState(DEFAULT_VISIBLE_FILTER_KEYS);
    const [pendingOpenFilterKey, setPendingOpenFilterKey] = useState(null);
    const filterDropdownRefs = useRef({});
    const textInputRefs = useRef({});
    const textTimerRef = useRef({});

    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 50,
        page: 0,
        sortField: "createdAt",
        sortOrder: -1,
    });

    const { isLoading, isFetching, isError, refetch } = useQuery(
        ["AiTasks", lazyState, filters],
        async () => {
            const searchBody = {
                first: lazyState.first,
                rows: lazyState.rows,
                sortField: lazyState.sortField,
                sortOrder: lazyState.sortOrder,
            };
            if (filters.status) {
                searchBody.status = filters.status;
            }
            if (filters.type) {
                searchBody.type = filters.type;
            }
            if (filters.owner) {
                searchBody.owner = filters.owner;
            }
            if (filters.providerId) {
                searchBody.providerId = filters.providerId.trim();
            }
            if (filters.errorText) {
                searchBody.errorText = filters.errorText.trim();
            }
            if (filters.hasError === true || filters.hasError === false) {
                searchBody.hasError = filters.hasError;
            }
            const dateRange = resolveCreatedDateRange(filters.date);
            if (dateRange) {
                searchBody.createdFrom = dateRange.createdFrom;
                searchBody.createdTo = dateRange.createdTo;
            }
            const result = await authApi.post("admin/ai-tasks/list", searchBody);
            setItems(result.data.items || []);
            setTotalRecords(result.data.totalRecords || 0);
            setLoaded(true);
            return result.data;
        }
    );

    useEffect(() => {
        if (!pendingOpenFilterKey) {
            return;
        }
        const openTimer = setTimeout(() => {
            const field = findFilterField(pendingOpenFilterKey);
            if (field && field.input) {
                const input = textInputRefs.current[pendingOpenFilterKey];
                if (input && typeof input.focus === "function") {
                    input.focus();
                }
            } else {
                const dropdown = filterDropdownRefs.current[pendingOpenFilterKey];
                if (dropdown && typeof dropdown.show === "function") {
                    dropdown.show();
                }
            }
            setPendingOpenFilterKey(null);
        }, 0);
        return () => {
            clearTimeout(openTimer);
        };
    }, [pendingOpenFilterKey, visibleFilterKeys]);

    useEffect(() => {
        return () => {
            const timers = textTimerRef.current;
            const keys = Object.keys(timers);
            for (let i = 0; i < keys.length; i++) {
                clearTimeout(timers[keys[i]]);
            }
        };
    }, []);

    const moreFilterOptions = useMemo(() => {
        const options = [];
        for (let i = 0; i < FILTER_FIELDS.length; i++) {
            const field = FILTER_FIELDS[i];
            if (visibleFilterKeys.indexOf(field.key) >= 0) {
                continue;
            }
            options.push({
                label: field.label,
                value: field.key,
            });
        }
        return options;
    }, [visibleFilterKeys]);

    const pageHeader = (
        <PageHeading pageTitle="Admin - AI Tasks">
            <div className="block" />
        </PageHeading>
    );

    if (isLoading && !loaded) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    const onPage = (event) => {
        setLazyState(event);
    };

    const onSort = (event) => {
        setLazyState(event);
    };

    const resetPaging = () => {
        setLazyState((prev) => ({
            ...prev,
            first: 0,
            page: 0,
        }));
    };

    const addFilterField = (key) => {
        setVisibleFilterKeys((prev) => {
            if (prev.indexOf(key) >= 0) {
                return prev;
            }
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                next.push(prev[i]);
            }
            next.push(key);
            return next;
        });
        setPendingOpenFilterKey(key);
    };

    const removeFilterField = (key) => {
        if (textTimerRef.current[key]) {
            clearTimeout(textTimerRef.current[key]);
            delete textTimerRef.current[key];
        }
        setVisibleFilterKeys((prev) => {
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                if (prev[i] !== key) {
                    next.push(prev[i]);
                }
            }
            return next;
        });
        setFilters((prev) => ({
            ...prev,
            [key]: emptyFilterValue(key),
        }));
        if (key === "providerId" || key === "errorText") {
            setTextInputs((prev) => ({
                ...prev,
                [key]: "",
            }));
        }
        resetPaging();
    };

    const onFilterFieldChange = (key, event) => {
        const value = Object.prototype.hasOwnProperty.call(event, "value") ? event.value : "";
        setFilters((prev) => ({
            ...prev,
            [key]: value == null && key !== "hasError" ? "" : value,
        }));
        resetPaging();
    };

    const onTextFilterChange = (key, value) => {
        setTextInputs((prev) => ({
            ...prev,
            [key]: value,
        }));
        if (textTimerRef.current[key]) {
            clearTimeout(textTimerRef.current[key]);
        }
        textTimerRef.current[key] = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                [key]: (value || "").trim(),
            }));
            resetPaging();
        }, 400);
    };

    const statusBody = (row) => (
        <Tag value={row.status || "—"} severity={statusSeverity(row.status)} />
    );

    const dateBody = (field) => (row) => formatDate(row[field]);

    const costBody = (row) => formatCostUsd(row.costUsd);
    const durationBody = (row) => formatDurationSeconds(row.durationMs);
    const queueTimeBody = (row) => formatDurationSeconds(row.queueTimeMs);

    const sumCostUsd = items.reduce((sum, row) => {
        const value = row?.costUsd;
        if (value == null || Number.isNaN(Number(value))) {
            return sum;
        }
        return sum + Number(value);
    }, 0);
    const sumDurationMs = items.reduce((sum, row) => {
        const value = row?.durationMs;
        if (value == null || Number.isNaN(Number(value))) {
            return sum;
        }
        return sum + Number(value);
    }, 0);
    const sumQueueTimeMs = items.reduce((sum, row) => {
        const value = row?.queueTimeMs;
        if (value == null || Number.isNaN(Number(value))) {
            return sum;
        }
        return sum + Number(value);
    }, 0);

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex flex-wrap align-items-center gap-2 py-3">
                        <div className="font-bold white-space-nowrap">Filter by</div>
                        {visibleFilterKeys.map((key) => {
                            const field = findFilterField(key);
                            if (!field) {
                                return null;
                            }
                            if (field.input) {
                                return (
                                    <div key={key} className="flex align-items-center gap-1">
                                        <InputText
                                            ref={(el) => {
                                                if (el) {
                                                    textInputRefs.current[key] = el;
                                                } else {
                                                    delete textInputRefs.current[key];
                                                }
                                            }}
                                            value={textInputs[key] || ""}
                                            onChange={(e) => onTextFilterChange(key, e.target.value)}
                                            placeholder={field.placeholder}
                                            className="w-14rem"
                                            aria-label={`Filter by ${field.label.toLowerCase()}`}
                                        />
                                        <Button
                                            type="button"
                                            icon="pi pi-times"
                                            className="p-button-text p-button-rounded p-button-sm"
                                            onClick={() => removeFilterField(key)}
                                            aria-label={`Remove ${field.label} filter`}
                                            tooltip={`Remove ${field.label}`}
                                            tooltipOptions={{ position: "top" }}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div key={key} className="flex align-items-center gap-1">
                                    <Dropdown
                                        ref={(el) => {
                                            if (el) {
                                                filterDropdownRefs.current[key] = el;
                                            } else {
                                                delete filterDropdownRefs.current[key];
                                            }
                                        }}
                                        value={filters[key]}
                                        options={field.options}
                                        onChange={(event) => onFilterFieldChange(key, event)}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder={field.placeholder}
                                        aria-label={`Filter by ${field.label.toLowerCase()}`}
                                    />
                                    <Button
                                        type="button"
                                        icon="pi pi-times"
                                        className="p-button-text p-button-rounded p-button-sm"
                                        onClick={() => removeFilterField(key)}
                                        aria-label={`Remove ${field.label} filter`}
                                        tooltip={`Remove ${field.label}`}
                                        tooltipOptions={{ position: "top" }}
                                    />
                                </div>
                            );
                        })}
                        {moreFilterOptions.length > 0 ? (
                            <Dropdown
                                value=""
                                options={moreFilterOptions}
                                onChange={(event) => {
                                    if (event.value) {
                                        addFilterField(event.value);
                                    }
                                }}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Filter..."
                                className="w-10rem"
                                aria-label="Add filter"
                            />
                        ) : null}
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-outlined"
                            onClick={() => refetch()}
                            loading={isFetching}
                            aria-label="Refresh"
                            tooltip="Refresh"
                            tooltipOptions={{ position: "top" }}
                        />
                    </div>
                    <DataTable
                        value={items}
                        dataKey="_id"
                        paginator
                        rows={lazyState.rows}
                        rowsPerPageOptions={[25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} AI tasks"
                        emptyMessage="No AI tasks found."
                        lazy
                        first={lazyState.first}
                        totalRecords={totalRecords}
                        onPage={onPage}
                        onSort={onSort}
                        sortField={lazyState.sortField}
                        sortOrder={lazyState.sortOrder}
                        loading={isLoading || isFetching}
                        selectionMode="single"
                        onRowClick={(e) => setSelectedTask(e.data)}
                        rowHover
                    >
                        <Column field="status" header="Status" body={statusBody} sortable style={{ width: "8rem" }} />
                        <Column field="type" header="Type" sortable />
                        <Column field="owner" header="Owner" sortable style={{ width: "7rem" }} />
                        <Column field="createdAt" header="Created" body={dateBody("createdAt")} sortable style={{ width: "10rem", whiteSpace: "nowrap" }} />
                        <Column
                            field="queueTimeMs"
                            header="Queue time"
                            body={queueTimeBody}
                            footer={formatDurationSeconds(sumQueueTimeMs)}
                            sortable
                            className="text-right"
                            headerClassName="right-aligned-header"
                            footerClassName="right-aligned-header"
                            style={{ width: "8rem" }}
                        />
                        <Column
                            field="durationMs"
                            header="Time taken"
                            body={durationBody}
                            footer={formatDurationSeconds(sumDurationMs)}
                            sortable
                            className="text-right"
                            headerClassName="right-aligned-header"
                            footerClassName="right-aligned-header"
                            style={{ width: "8rem" }}
                        />
                        <Column
                            field="costUsd"
                            header="Cost"
                            body={costBody}
                            footer={formatCostUsd(sumCostUsd)}
                            sortable
                            className="text-right"
                            headerClassName="right-aligned-header"
                            footerClassName="right-aligned-header"
                            style={{ width: "8rem" }}
                        />
                    </DataTable>
                </div>
            </div>

            <Dialog
                header="AI Task Detail"
                visible={!!selectedTask}
                style={{ width: "min(720px, 95vw)" }}
                modal
                onHide={() => setSelectedTask(null)}
            >
                {selectedTask && (
                    <div className="grid">
                        {[
                            ["ID", selectedTask._id],
                            ["Status", selectedTask.status],
                            ["Type", selectedTask.type],
                            ["Owner", selectedTask.owner],
                            ["Provider ID", selectedTask.providerId],
                            ["Section ID", selectedTask.sectionId],
                            ["Slide ID", selectedTask.slideId],
                            ["Slide order", selectedTask.slideOrder],
                            ["Element ID", selectedTask.elementId],
                            ["Page ID", selectedTask.pageId],
                            ["Offer number", selectedTask.offerNumber],
                            ["Created by", selectedTask.createdBy],
                            ["Created", formatDate(selectedTask.createdAt)],
                            ["Started", formatDate(selectedTask.startedAt)],
                            ["Completed", formatDate(selectedTask.completedAt)],
                            ["Cost", formatCostUsd(selectedTask.costUsd)],
                            ["Queue time", formatDurationSeconds(selectedTask.queueTimeMs)],
                            ["Time taken", formatDurationSeconds(selectedTask.durationMs)],
                        ].map(([label, value]) => (
                            <div className="col-12 md:col-6" key={label}>
                                <div className="text-500 text-sm">{label}</div>
                                <div className="font-medium word-break-all">{value == null || value === "" ? "—" : String(value)}</div>
                            </div>
                        ))}
                        <div className="col-12">
                            <div className="text-500 text-sm mb-2">Steps</div>
                            {selectedTask.steps && selectedTask.steps.length > 0 ? (
                                <div className="flex flex-column gap-2">
                                    {selectedTask.steps.map((step, index) => (
                                        <div key={`${step.type || "step"}-${index}`} className="p-2 surface-100 border-round">
                                            <div className="font-medium">{formatStepType(step.type)}</div>
                                            <div className="text-sm text-600">
                                                Model: {step.model || "—"}
                                            </div>
                                            <div className="text-sm text-600">
                                                Time: {formatDurationSeconds(step.durationMs)}
                                            </div>
                                            <div className="text-sm text-600">
                                                Cost: {formatCostUsd(step.costUsd)}
                                            </div>
                                            {(step.inputTokens != null || step.outputTokens != null) && (
                                                <div className="text-sm text-600">
                                                    Tokens: in {step.inputTokens ?? "—"} / out {step.outputTokens ?? "—"}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-500">—</div>
                            )}
                        </div>
                        <div className="col-12">
                            <div className="text-500 text-sm">Error message</div>
                            <pre className="white-space-pre-wrap m-0 p-2 surface-100 border-round" style={{ maxHeight: "240px", overflow: "auto" }}>
                                {selectedTask.errorMessage || "—"}
                            </pre>
                        </div>
                        {selectedTask.roughInput ? (
                            <div className="col-12">
                                <div className="text-500 text-sm">Rough input</div>
                                <pre className="white-space-pre-wrap m-0 p-2 surface-100 border-round" style={{ maxHeight: "160px", overflow: "auto" }}>
                                    {selectedTask.roughInput}
                                </pre>
                            </div>
                        ) : null}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default AiTasks;
